import { supabase } from './supabase'

export interface SubscriptionLimits {
  plan: 'trial' | 'starter' | 'growth' | 'leadership' | 'custom'
  max_members: number
  max_admins: number
  max_storage_gb: number
  max_sms_monthly: number
  trial_sms_total?: number
  finance_enabled: boolean
  attendance_enabled: boolean
  advanced_ministry_enabled: boolean
  complete_finance_enabled: boolean
  qr_attendance_enabled: boolean
  ministry_scheduling_enabled: boolean
  hr_payroll_enabled: boolean
  media_library_enabled: boolean
  mobile_app_enabled: boolean
  api_access_enabled: boolean
  multi_location_enabled: boolean
  max_locations: number
}

export interface UsageStats {
  current_members_count: number
  current_admins_count: number
  current_storage_mb: number
  current_month_sms_sent: number
  members_usage_percent: number
  storage_usage_percent: number
  sms_usage_percent: number
}

export interface SubscriptionStatus {
  status: 'trial' | 'active' | 'past_due' | 'cancelled' | 'expired'
  subscription_expires_at: string | null
  days_until_expiry: number | null
}

/**
 * Get subscription limits for a church
 */
export async function getSubscriptionLimits(churchId: string): Promise<SubscriptionLimits | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_subscription_limits', { p_church_id: churchId })
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error fetching subscription limits:', error)
    return null
  }
}

/**
 * Check if a specific feature is enabled for a church
 */
export async function isFeatureEnabled(churchId: string, featureName: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .rpc('is_feature_enabled', {
        p_church_id: churchId,
        p_feature_name: featureName
      })

    if (error) throw error

    return data || false
  } catch (error) {
    console.error('Error checking feature access:', error)
    return false
  }
}

/**
 * Check if church can add more members
 */
export async function canAddMember(churchId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .rpc('can_add_member', { p_church_id: churchId })

    if (error) throw error

    return data || false
  } catch (error) {
    console.error('Error checking member limit:', error)
    return false
  }
}

/**
 * Get current usage stats for a church
 */
export async function getUsageStats(churchId: string): Promise<UsageStats | null> {
  try {
    const currentMonth = new Date().getMonth() + 1
    const currentYear = new Date().getFullYear()

    const { data, error } = await supabase
      .from('subscription_usage_tracking')
      .select('*')
      .eq('church_id', churchId)
      .eq('tracking_month', currentMonth)
      .eq('tracking_year', currentYear)
      .single()

    if (error) {
      // If no usage record exists yet, return zeros
      if (error.code === 'PGRST116') {
        return {
          current_members_count: 0,
          current_admins_count: 0,
          current_storage_mb: 0,
          current_month_sms_sent: 0,
          members_usage_percent: 0,
          storage_usage_percent: 0,
          sms_usage_percent: 0,
        }
      }
      throw error
    }

    // Calculate usage percentages
    const limits = await getSubscriptionLimits(churchId)
    if (!limits) return data

    const membersPercent = limits.max_members === 999999
      ? 0
      : (data.current_members_count / limits.max_members) * 100

    const storagePercent = limits.max_storage_gb === 999999
      ? 0
      : (data.current_storage_mb / (limits.max_storage_gb * 1024)) * 100

    const smsPercent = (data.current_month_sms_sent / limits.max_sms_monthly) * 100

    return {
      ...data,
      members_usage_percent: Math.round(membersPercent * 100) / 100,
      storage_usage_percent: Math.round(storagePercent * 100) / 100,
      sms_usage_percent: Math.round(smsPercent * 100) / 100,
    }
  } catch (error) {
    console.error('Error fetching usage stats:', error)
    return null
  }
}

/**
 * Get subscription status for a church
 */
export async function getSubscriptionStatus(churchId: string): Promise<SubscriptionStatus | null> {
  try {
    const { data, error } = await supabase
      .from('church_subscriptions')
      .select('status, subscription_expires_at')
      .eq('church_id', churchId)
      .single()

    if (error) throw error

    let daysUntilExpiry = null
    if (data.subscription_expires_at) {
      const expiryDate = new Date(data.subscription_expires_at)
      const today = new Date()
      const diffTime = expiryDate.getTime() - today.getTime()
      daysUntilExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }

    return {
      status: data.status,
      subscription_expires_at: data.subscription_expires_at,
      days_until_expiry: daysUntilExpiry,
    }
  } catch (error) {
    console.error('Error fetching subscription status:', error)
    return null
  }
}

/**
 * Check if church can add more admins
 */
export async function canAddAdmin(churchId: string): Promise<{ canAdd: boolean; current: number; max: number }> {
  try {
    // Get limits
    const limits = await getSubscriptionLimits(churchId)
    if (!limits) {
      return { canAdd: false, current: 0, max: 0 }
    }

    // Count current admins
    const { count, error } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('church_id', churchId)
      .in('admin_level', ['church_owner', 'church_admin', 'church_elder', 'ministry_leader'])

    if (error) throw error

    const currentAdmins = count || 0
    const canAdd = limits.max_admins === 999999 || currentAdmins < limits.max_admins

    return {
      canAdd,
      current: currentAdmins,
      max: limits.max_admins,
    }
  } catch (error) {
    console.error('Error checking admin limit:', error)
    return { canAdd: false, current: 0, max: 0 }
  }
}

/**
 * Check if church can send SMS
 */
export async function canSendSMS(churchId: string, count: number = 1): Promise<{ canSend: boolean; remaining: number }> {
  try {
    const limits = await getSubscriptionLimits(churchId)
    const usage = await getUsageStats(churchId)

    if (!limits || !usage) {
      return { canSend: false, remaining: 0 }
    }

    const remaining = limits.max_sms_monthly - usage.current_month_sms_sent
    const canSend = remaining >= count

    return { canSend, remaining }
  } catch (error) {
    console.error('Error checking SMS limit:', error)
    return { canSend: false, remaining: 0 }
  }
}

/**
 * Update usage tracking for a church
 */
export async function updateUsageTracking(churchId: string): Promise<void> {
  try {
    await supabase.rpc('update_usage_tracking', { p_church_id: churchId })
  } catch (error) {
    console.error('Error updating usage tracking:', error)
  }
}

/**
 * Get feature access summary for a church
 */
export async function getFeatureAccess(churchId: string) {
  const limits = await getSubscriptionLimits(churchId)

  if (!limits) {
    return {
      finance: false,
      attendance: false,
      advancedMinistry: false,
      completeFinance: false,
      qrAttendance: false,
      ministryScheduling: false,
      hrPayroll: false,
      mediaLibrary: false,
      mobileApp: false,
      apiAccess: false,
      multiLocation: false,
    }
  }

  return {
    finance: limits.finance_enabled,
    attendance: limits.attendance_enabled,
    advancedMinistry: limits.advanced_ministry_enabled,
    completeFinance: limits.complete_finance_enabled,
    qrAttendance: limits.qr_attendance_enabled,
    ministryScheduling: limits.ministry_scheduling_enabled,
    hrPayroll: limits.hr_payroll_enabled,
    mediaLibrary: limits.media_library_enabled,
    mobileApp: limits.mobile_app_enabled,
    apiAccess: limits.api_access_enabled,
    multiLocation: limits.multi_location_enabled,
  }
}

/**
 * Check if trial has expired
 */
export async function isTrialExpired(churchId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_trial_expired', {
      p_church_id: churchId,
    })

    if (error) throw error
    return data || false
  } catch (error) {
    console.error('Error checking trial expiry:', error)
    return false
  }
}

/**
 * Check if in grace period
 */
export async function isInGracePeriod(churchId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_in_grace_period', {
      p_church_id: churchId,
    })

    if (error) throw error
    return data || false
  } catch (error) {
    console.error('Error checking grace period:', error)
    return false
  }
}

/**
 * Get days remaining in trial
 */
export async function getTrialDaysRemaining(churchId: string): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('get_trial_days_remaining', {
      p_church_id: churchId,
    })

    if (error) throw error
    return data || 0
  } catch (error) {
    console.error('Error getting trial days remaining:', error)
    return 0
  }
}

/**
 * Upgrade from trial to paid plan
 */
export async function upgradeFromTrial(
  churchId: string,
  newPlan: 'starter' | 'growth' | 'leadership' | 'custom',
  billingCycle: 'monthly' | 'annual' | 'quarterly',
  paymentMethod?: string,
  setupFeePaid: boolean = false
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.rpc('upgrade_from_trial', {
      p_church_id: churchId,
      p_new_plan: newPlan,
      p_billing_cycle: billingCycle,
      p_payment_method: paymentMethod,
      p_setup_fee_paid: setupFeePaid,
    })

    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Error upgrading from trial:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upgrade subscription',
    }
  }
}

/**
 * Get all plan configurations
 */
export async function getAllPlanConfigs() {
  try {
    const { data, error } = await supabase
      .from('subscription_plan_config')
      .select('*')
      .eq('is_active', true)
      .order('monthly_price', { ascending: true })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching plan configs:', error)
    return []
  }
}

/**
 * Get pricing for a specific plan
 */
export async function getPlanPricing(plan: string) {
  try {
    const { data, error } = await supabase
      .from('subscription_plan_config')
      .select('*')
      .eq('plan', plan)
      .eq('is_active', true)
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error fetching plan pricing:', error)
    return null
  }
}
